import { Flex, IconButton, Text, VStack, useColorModeValue } from "@chakra-ui/react";
import { DownloadIcon } from "@chakra-ui/icons";
import { keyframes } from "@emotion/react";
import MorphingBrandLogo from "components/branding/MorphingBrandLogo";

type PWASplashScreenProps = {
  isVisible: boolean;
  iconSrc: string;
  brandName: string;
  installAvailable: boolean;
  onInstallClick: () => void;
  showInstallHint: boolean;
};

const splashDrift = keyframes`
  0%, 100% { transform: translate3d(-2%, 1%, 0) scale(1); opacity: 0.58; }
  50% { transform: translate3d(2%, -2%, 0) scale(1.08); opacity: 0.88; }
`;

const splashOrbit = keyframes`
  0% { transform: rotate(0deg) scale(0.96); }
  50% { transform: rotate(180deg) scale(1.03); }
  100% { transform: rotate(360deg) scale(0.96); }
`;

const splashEntrance = keyframes`
  0% { opacity: 0; transform: translateY(16px) scale(0.94); filter: blur(8px); }
  100% { opacity: 1; transform: translateY(0) scale(1); filter: blur(0); }
`;

export default function PWASplashScreen({
  isVisible,
  iconSrc,
  brandName,
  installAvailable,
  onInstallClick,
  showInstallHint,
}: PWASplashScreenProps) {
  const bgColor = useColorModeValue(
    "linear(145deg, #06111f 0%, #081421 48%, #07101d 100%)",
    "linear(145deg, #06111f 0%, #081421 48%, #07101d 100%)"
  );

  if (!isVisible) {
    return null;
  }

  return (
    <Flex
      position="fixed"
      top="0"
      left="0"
      right="0"
      bottom="0"
      minH="100dvh"
      bgGradient={bgColor}
      zIndex={4000}
      align="center"
      justify="center"
      px={6}
      pt={6}
      pb="calc(env(safe-area-inset-bottom, 0px) + 24px)"
      style={{
        willChange: "opacity",
        transition: "opacity 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
      }}
      _before={{
        content: '""',
        position: "absolute",
        width: "min(72vw, 420px)",
        height: "min(72vw, 420px)",
        borderRadius: "999px",
        bg: "radial-gradient(circle, rgba(56,189,248,0.18) 0%, rgba(99,102,241,0.10) 38%, transparent 72%)",
        filter: "blur(18px)",
        animation: `${splashDrift} 3.8s ease-in-out infinite`,
        pointerEvents: "none",
      }}
      _after={{
        content: '""',
        position: "absolute",
        width: "min(76vw, 450px)",
        height: "min(76vw, 450px)",
        borderRadius: "999px",
        bg: "conic-gradient(from 90deg, transparent 0deg, rgba(56,189,248,0.0) 86deg, rgba(56,189,248,0.28) 126deg, transparent 174deg, transparent 360deg)",
        opacity: 0.4,
        animation: `${splashOrbit} 5.6s linear infinite`,
        pointerEvents: "none",
      }}
    >
      <VStack
        spacing={5}
        position="relative"
        zIndex={1}
        sx={{ animation: `${splashEntrance} 680ms cubic-bezier(0.2, 0.8, 0.2, 1) both` }}
      >
        {iconSrc ? (
          <MorphingBrandLogo
            iconSrc={iconSrc}
            alt={brandName || "OrgCloud"}
            isActive={isVisible}
          />
        ) : null}
        {brandName ? (
          <Text
            color="whiteAlpha.950"
            fontSize={{ base: "xl", md: "2xl" }}
            fontWeight="700"
            letterSpacing="0.01em"
            textAlign="center"
            maxW="min(82vw, 320px)"
          >
            {brandName}
          </Text>
        ) : null}
      </VStack>

      {/* Botón de instalación discreto (solo si está disponible) */}
      {installAvailable && (
        <IconButton
          aria-label="Install application"
          icon={<DownloadIcon />}
          onClick={onInstallClick}
          position="absolute"
          bottom={{ base: 6, md: 8 }}
          right={{ base: 6, md: 8 }}
          size={{ base: "md", md: "lg" }}
          borderRadius="full"
          colorScheme="blue"
          variant="solid"
          opacity={0.8}
          _hover={{ opacity: 1, transform: "scale(1.1)" }}
          style={{
            willChange: "transform, opacity",
            transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
          }}
        />
      )}

    </Flex>
  );
}
